import React from 'react';
import { ChangeOrderFull } from '../../services/change-management.service';

interface ChangeOrderListProps {
  orders: ChangeOrderFull[];
  onSelect: (order: ChangeOrderFull) => void;
  onApprove?: (id: string) => Promise<void>;
  onReject?: (id: string, reason: string) => Promise<void>;
  onExecute?: (id: string) => Promise<void>;
  onViewSignature?: (order: ChangeOrderFull) => void;
}

export function ChangeOrderList({
  orders,
  onSelect,
  onApprove,
  onReject,
  onExecute,
  onViewSignature
}: ChangeOrderListProps) {
  const getStatusColor = (status: string): string => {
    const colors: Record<string, string> = {
      draft: 'bg-slate-100 text-slate-700',
      pending: 'bg-yellow-100 text-yellow-800',
      client_approved: 'bg-green-100 text-green-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      executed: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      draft: 'Borrador',
      pending: 'Pendiente',
      client_approved: 'Aprobado por cliente',
      approved: 'Aprobado',
      rejected: 'Rechazado',
      executed: 'Ejecutado'
    };
    return labels[status] || status;
  };

  const getReasonLabel = (reason: string): string => {
    const labels: Record<string, string> = {
      design_change: 'Cambio de diseño',
      material_change: 'Cambio de materiales',
      scope_change: 'Cambio de alcance',
      unforeseen: 'Imprevisto'
    };
    return labels[reason] || reason;
  };

  if (orders.length === 0) {
    return (
      <div className="text-center py-8 bg-white rounded-lg shadow">
        <p className="text-gray-500">No hay órdenes de cambio registradas</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {orders.map((order) => {
        const impact = order.impact || { impact: 0, newBudget: 0, originalBudget: 0, impactPercentage: 0 } as any;
        const hasClientSignature = !!order.clientSignatureName || !!order.clientSignatureSvg;

        return (
          <div
            key={order.id}
            className="bg-white rounded-lg shadow p-4 hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => onSelect(order)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  {order.number && (
                    <span className="text-xs font-mono bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                      {order.number}
                    </span>
                  )}
                  <h4 className="font-semibold text-gray-800 truncate">
                    {order.title || 'Cambio sin título'}
                  </h4>
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.status)}`}>
                    {getStatusLabel(order.status)}
                  </span>
                </div>

                <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                  {order.description}
                </p>

                <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                  <span>📦 {order.items?.length || 0} partidas</span>
                  <span>💰 {impact.impact >= 0 ? '+' : ''}${impact.impact.toFixed(2)}</span>
                  {order.requestedByName && (
                    <span>👤 {order.requestedByName}</span>
                  )}
                  <span className="text-gray-400 text-xs">
                    {new Date(order.createdAt).toLocaleDateString('es-MX')}
                  </span>
                </div>

                {order.status === 'client_approved' && hasClientSignature && (
                  <div className="mt-2 text-xs text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1 inline-block">
                    ✅ Aprobado por {order.clientSignatureName}
                    {order.clientApprovedAt && ` el ${new Date(order.clientApprovedAt).toLocaleDateString('es-MX')}`}
                  </div>
                )}

                {order.status === 'rejected' && (order.clientRejectionReason || order.rejectedReason) && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 inline-block">
                    ❌ Motivo: {order.clientRejectionReason || order.rejectedReason}
                  </div>
                )}

                {order.notificationSentAt && order.status === 'pending' && (
                  <div className="mt-2 text-xs text-blue-700">
                    📩 Cliente notificado el {new Date(order.notificationSentAt).toLocaleString('es-MX')}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 shrink-0">
                {order.status === 'pending' && onApprove && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onApprove(order.id);
                    }}
                    className="px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors font-medium"
                  >
                    ✅ Aprobar
                  </button>
                )}

                {order.status === 'client_approved' && onViewSignature && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewSignature(order);
                    }}
                    className="px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100 transition-colors font-medium whitespace-nowrap"
                  >
                    ✍️ Ver firma
                  </button>
                )}

                {(order.status === 'client_approved' || order.status === 'approved') && onExecute && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onExecute(order.id);
                    }}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary-dark transition-colors font-medium"
                  >
                    📋 Ejecutar
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}